import { ExpertDisplayName } from "@/components/display/ExpertDisplayName";
import { Button, buttonVariants } from "@/components/ui/button";
import { useSettingData } from "@/data/use-settings-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faPencil,
  faPlus,
  faXmark,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React, { useState } from "react";
import { AvailableExpertDropdown } from "./AvailableExpertDropdown";
import {
  MAX_EXPERTS_IN_CHAT,
  useExpertSelection,
} from "./ExpertSelectionContext";
import { InviteExpertDialog } from "./InviteExpertDialog";

type ExpertSelectionRowProps = {
  className?: string;
};

export const ExpertSelectionRow: React.FC<ExpertSelectionRowProps> = ({
  className,
}) => {
  const groupUuid = useGroupUuid();
  const { selectedExperts, disableSelection, unselectExpert } =
    useExpertSelection();
  const { value: multipleExpertFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_MULTIPLE_EXPERTS_CHAT",
  );
  const isMultipleExpertsEnabled = multipleExpertFeatureFlag === "enabled";

  const [openDropdown, setOpenDropdown] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);

  const handleInviteExpertClick = () => {
    setOpenInviteDialog(true);
  };

  const handleRemoveExpert = (expertUuid: string) => {
    unselectExpert(expertUuid);
  };

  return (
    <>
      <div
        data-testid="expert-selection-row"
        className={cn(
          "flex cursor-pointer flex-row gap-4 py-2 text-sm",
          className,
        )}
        onClick={() => {
          if (!disableSelection) {
            setOpenDropdown(true);
          }
        }}
      >
        <div className="text-foreground-5 mt-[7px] uppercase">To:</div>
        <div className="flex flex-row flex-wrap gap-2">
          {selectedExperts.length === 0 && (
            <div className="text-foreground-5 mt-[8px] italic">
              {isMultipleExpertsEnabled
                ? "Select experts..."
                : "Select expert..."}
            </div>
          )}
          {selectedExperts.map((expert) => {
            return (
              <div
                key={expert.uuid}
                className={cn(
                  buttonVariants({ variant: "secondary" }),
                  "bg-background px-2",
                )}
              >
                <ExpertDisplayName expert={expert} />
                {isMultipleExpertsEnabled && (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove Expert ${expert.name}`}
                    title={`Remove Expert ${expert.name}`}
                    onClick={(e) => {
                      handleRemoveExpert(expert.uuid);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                )}
              </div>
            );
          })}
          <AvailableExpertDropdown
            open={openDropdown}
            onOpenChange={setOpenDropdown}
            onInviteExpertClick={handleInviteExpertClick}
            disabled={disableSelection}
          >
            <Button
              size="icon"
              variant="ghost"
              aria-label="Open Expert Selection Panel"
              title="Open Expert Selection Panel"
            >
              <FontAwesomeIcon
                icon={
                  isMultipleExpertsEnabled
                    ? selectedExperts.length >= MAX_EXPERTS_IN_CHAT
                      ? faPencil
                      : faPlus
                    : faChevronDown
                }
              />
            </Button>
          </AvailableExpertDropdown>
        </div>
      </div>
      <InviteExpertDialog
        open={openInviteDialog}
        onOpenChange={setOpenInviteDialog}
      />
    </>
  );
};
